// Reusable application sidebar.
//
// This is the single source of truth for in-app navigation. Every
// authenticated page should compose it (typically by sitting next to
// the <main> slot of <Layout />) so the look, behaviour and route
// definitions stay consistent as the product grows.
//
// Design goals:
//   - SCALABLE: navigation is data-driven via the SIDEBAR_SECTIONS
//     constant below — add or reorder entries in one place and every
//     page that mounts <Sidebar /> picks them up.
//   - CONSISTENT: visual language matches <Button />, <Input /> and
//     <Layout /> — slate base, green/emerald accent, rounded-xl,
//     focus-visible ring-green-400, dark-mode parity.
//   - SELF-CONTAINED: no external icon dependency (inline SVGs),
//     no global state — route highlighting is delegated to
//     react-router-dom's <NavLink />, expand/collapse uses local
//     useState, and the Account block reads from useCurrentUser().
//   - RESPONSIVE: persistent on md+ screens, slide-in drawer on
//     mobile triggered by an internal hamburger button.

import { useState } from 'react';
import type { ReactElement, ReactNode, SVGProps } from 'react';
import { NavLink } from 'react-router-dom';

import { useCurrentUser } from '../../features/profile';

// ---------------------------------------------------------------------------
// Icons — kept inline so the design system stays dependency-free. Each icon
// accepts the standard SVGProps so callers can tweak size / colour via
// Tailwind className without us reaching for a 3rd-party library.
// ---------------------------------------------------------------------------

const iconBaseProps: SVGProps<SVGSVGElement> = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

const DashboardIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
);

const RiskIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M12 3 2 21h20L12 3Z" />
        <path d="M12 10v5" />
        <path d="M12 18h.01" />
    </svg>
);

const PortfolioIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M3 3v18h18" />
        <path d="M7 15l3-4 3 3 5-7" />
    </svg>
);

// Drift Auditor PRO — a balance-scale pictogram (rebalancing / allocation
// discipline) so the institutional auditor module reads as "keep the book in
// balance" at a glance.
const DriftAuditIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M12 3v18" />
        <path d="M5 7h14" />
        <path d="M7 7 4 13a3 3 0 0 0 6 0L7 7Z" />
        <path d="M17 7l-3 6a3 3 0 0 0 6 0l-3-6Z" />
        <path d="M8 21h8" />
    </svg>
);

// Portfolio Optimizer PRO — a tuning / optimization pictogram (concentric
// target behind adjustment sliders) so the institutional diversification &
// optimization engine reads as "dial in the optimal allocation" at a glance.
const OptimizerIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v4" />
        <path d="M12 17v4" />
        <path d="M3 12h4" />
        <path d="M17 12h4" />
        <circle cx="12" cy="12" r="2.5" />
    </svg>
);

const GoalIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
    </svg>
);

const ReportsIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
        <path d="M14 3v5h5" />
        <path d="M9 13h6" />
        <path d="M9 17h6" />
    </svg>
);

const InsightsIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M9 21h6" />
        <path d="M10 17h4" />
        <path d="M12 3a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2v.3h6V18c0-.8.4-1.5 1-2A7 7 0 0 0 12 3Z" />
    </svg>
);

// Market Analysis — a rising yield-curve pictogram (axes + upward-sweeping
// line with a marker) so the Treasury Curve module reads as macro/rates
// analytics at a glance.
const MarketAnalysisIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M3 3v18h18" />
        <path d="M6 16c3-1 5-6 8-7s4-1 7-3" />
        <circle cx="14" cy="9" r="1.4" />
    </svg>
);

// --- Asset Class Rankings — distinct pictograms per asset class so the
//     six entries are scannable at a glance instead of sharing one icon. ---

const EquitiesIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M3 3v18h18" />
        <path d="M7 14l3-3 3 2 5-6" />
        <path d="M18 7h-3" />
        <path d="M18 7v3" />
    </svg>
);

const BondIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
        <circle cx="12" cy="14" r="1.5" />
    </svg>
);

const ReitIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M4 21V8l6-4 6 4v13" />
        <path d="M16 21V11l4 3v7" />
        <path d="M8 12h.01" />
        <path d="M8 16h.01" />
        <path d="M12 12h.01" />
        <path d="M12 16h.01" />
    </svg>
);

const CommoditiesIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <rect x="3" y="14" width="5" height="6" rx="1" />
        <rect x="9.5" y="9" width="5" height="11" rx="1" />
        <rect x="16" y="4" width="5" height="16" rx="1" />
    </svg>
);

const CryptoIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M12 2 3 7v10l9 5 9-5V7l-9-5Z" />
        <path d="M9.5 9.5h4a2 2 0 0 1 0 4h-4Z" />
        <path d="M9.5 13.5h4.3a2 2 0 0 1 0 4H9.5Z" />
        <path d="M11 7.5v1.5M11 17.5V19" />
    </svg>
);

const FxIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M4 8h13" />
        <path d="m14 5 3 3-3 3" />
        <path d="M20 16H7" />
        <path d="m10 13-3 3 3 3" />
    </svg>
);

const ScreenerIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="10" y1="18" x2="14" y2="18" />
        <circle cx="9" cy="6" r="2" />
        <circle cx="14" cy="12" r="2" />
    </svg>
);

// UCITS ETFs — a euro symbol inside a circle so the European UCITS
// screener / alpha engine reads as "regulated EU funds" at a glance.
const UcitsEtfIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <circle cx="12" cy="12" r="9" />
        <path d="M15 9.2a3.5 3.5 0 1 0 0 5.6" />
        <path d="M7.5 11h5" />
        <path d="M7.5 13.4h5" />
    </svg>
);

const SettingsIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
);

const ChevronIcon = ({
    open,
    ...rest
}: SVGProps<SVGSVGElement> & { open: boolean }) => (
    <svg
        {...iconBaseProps}
        width={16}
        height={16}
        {...rest}
        className={[
            'transition-transform duration-200',
            open ? 'rotate-180' : 'rotate-0',
            rest.className ?? '',
        ].join(' ')}
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const MenuIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
    </svg>
);

const CloseIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg {...iconBaseProps} {...props}>
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
    </svg>
);

// ---------------------------------------------------------------------------
// Navigation model.
//
// Navigation is grouped into labeled NavSections. Each section may carry an
// optional ``groupTitle`` header and may be ``collapsible`` (toggle the
// items open/closed). Sections are visually separated by a thin divider.
// To add a new entry: append to the right section's ``items``. To add a new
// section: push another NavSection onto SIDEBAR_SECTIONS.
// ---------------------------------------------------------------------------

export interface NavItem {
    label: string;
    to: string;
    icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
    /** Optional pill rendered right-aligned on the row (e.g. "Popular"). */
    badge?: string;
}

export interface NavSection {
    /** Optional labeled header rendered above the section's items. */
    groupTitle?: string;
    /** When true, the header becomes a button that toggles item visibility. */
    collapsible?: boolean;
    /** Initial open state when ``collapsible`` (defaults to open). */
    defaultOpen?: boolean;
    items: NavItem[];
}

export const SIDEBAR_SECTIONS: NavSection[] = [
    {
        groupTitle: 'Overview',
        items: [
            // Feature-based routing: every product module lives under
            // /dashboard/* so the Layout / Sidebar shell is reused without
            // a top-level route swap.
            { label: 'Dashboard', to: '/dashboard', icon: DashboardIcon },
            // Personal transaction-ledger tracker (FIFO cost-basis + live P&L).
            {
                label: 'My Portfolios',
                to: '/dashboard/my-portfolios',
                icon: PortfolioIcon,
                badge: 'Popular',
            },
            {
                label: 'Global Screener',
                to: '/dashboard/screener',
                icon: ScreenerIcon,
                badge: 'Popular',
            },
            // UCITS Alpha Engine — European UCITS ETF breakout ideas,
            // in-memory Polars screener and Numba sector-overlap similarity.
            {
                label: 'UCITS ETFs',
                to: '/dashboard/ucits-etf',
                icon: UcitsEtfIcon,
                badge: 'Hot',
            },
        ],
    },
    {
        groupTitle: 'Risk Intelligence Suite',
        collapsible: true,
        defaultOpen: true,
        items: [
            // ``/dashboard/risk-assessment`` lands on the suite portal; the
            // wizard itself sits at ``/dashboard/risk-assessment/test``.
            { label: 'Risk Assessment', to: '/dashboard/risk-assessment', icon: RiskIcon },
            // Live tool — pulls the recommended allocation from the
            // risk-assessment results and runs predefined market-shock
            // scenarios against it. See features/portfolio-stress-test.
            {
                label: 'Portfolio Stress Test',
                to: '/dashboard/portfolio-stress-test',
                icon: PortfolioIcon,
                badge: 'Popular',
            },
            // Goal-based Monte Carlo: same recommended allocation, but
            // projected forward across thousands of paths to answer
            // "will I hit my goal?". See features/goal-based-risk-alignment-tool.
            {
                label: 'Goal-Based Risk Alignment',
                to: '/dashboard/goal-based-risk-alignment-tool',
                icon: GoalIcon,
                badge: 'Hot',
            },
        ],
    },
    {
        groupTitle: 'Portfolio Construction',
        collapsible: true,
        defaultOpen: true,
        items: [
            // Single-domain Black-Litterman glass-box (pre-fetch & slide).
            {
                label: 'Stock-Only Portfolio',
                to: '/dashboard/stock-only-portfolio',
                icon: PortfolioIcon,
                badge: 'Hot',
            },
            // Interactive 3-tier cross-asset "What-If" cockpit.
            {
                label: 'Multi-Asset Optimizer',
                to: '/dashboard/multi-asset-portfolio-optimizer',
                icon: PortfolioIcon,
                badge: 'Hot',
            },
            // Saved-portfolios master hub: efficient-frontier explorer +
            // nightly live-tracking daemon management.
            {
                label: 'My Strategies',
                to: '/dashboard/my-strategies',
                icon: ReportsIcon,
                badge: 'Hot',
            },
            // My Portfolio Auditor PRO — institutional tax-lot, cash-flow and
            // AI regime-aware drift & rebalancing auditor. Six backtests, an
            // 8-point corridor frontier, a historical AI trade ledger and a
            // live execution blotter.
            {
                label: 'My Portfolio Auditor PRO',
                to: '/dashboard/portfolio-drift-auditor-pro',
                icon: DriftAuditIcon,
                badge: 'HOT',
            },
            // My Portfolio Optimizer PRO — institutional 5-pillar
            // diversification & optimization engine: correlation/tail
            // contagion, Euler risk attribution, multi-factor OLS,
            // Cornish-Fisher tail stress and six rebalancing simulations.
            {
                label: 'My Portfolio Optimizer',
                to: '/dashboard/portfolio-diversification-optimizer-pro',
                icon: OptimizerIcon,
                badge: 'PRO',
            },
        ],
    },
    {
        groupTitle: 'Asset Class Rankings',
        collapsible: true,
        defaultOpen: true,
        items: [
            { label: 'Equities (Stocks)', to: '/dashboard/stock-ranking', icon: EquitiesIcon },
            { label: 'Bond ETFs', to: '/dashboard/etf-ranking', icon: BondIcon },
            { label: 'REITs', to: '/dashboard/reits-ranking', icon: ReitIcon },
            { label: 'Commodities', to: '/dashboard/commodities-ranking', icon: CommoditiesIcon },
            { label: 'Crypto', to: '/dashboard/crypto-ranking', icon: CryptoIcon },
            { label: 'FX & Currencies', to: '/dashboard/fx-ranking', icon: FxIcon },
        ],
    },
    {
        groupTitle: 'Knowledge',
        items: [
            // Market Analysis — the institutional Treasury Yield Curve
            // dashboard (features/market-analysis/treasury-curve). Spreads,
            // curve regimes, momentum and macro volatility analytics.
            {
                label: 'Market Analysis',
                to: '/dashboard/market-analysis',
                icon: MarketAnalysisIcon,
                badge: 'Popular',
            },
            // Sector Rotation — institutional relative-strength analytics
            // (features/market-analysis/sector-rotation): JdK Relative Rotation
            // Graphs, macro-cycle sector tilts fused with the AFML transition
            // signal, and SciPy Ward-clustered correlation matrices.
            {
                label: 'Sector Rotation',
                to: '/dashboard/market-analysis/sector-rotation',
                icon: MarketAnalysisIcon,
                badge: 'New',
            },
            // Insider Trading Radar — Top-20 liquid insider Purchases / Sales
            // (features/insider-trading).
            {
                label: 'Insider Trading',
                to: '/dashboard/insider-trading',
                icon: InsightsIcon,
                badge: 'NEW',
            },
            // Wealth-Building Assistant — streaming LLM education chat
            // (features/wealth-building-assistant-chat).
            {
                label: 'Wealth Assistant',
                to: '/dashboard/wealth-assistant',
                icon: InsightsIcon,
                badge: 'NEW',
            },
            // Momentum Insights — the daily Top-10 Buy / Top-10 Sell breakout
            // dashboard (features/insights). Lives under /dashboard/* like
            // every other feature so the Layout / Sidebar shell is reused.
            { label: 'Insights', to: '/dashboard/insights', icon: InsightsIcon, badge: 'Hot' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Atoms — kept private to this file. Hoisting them into their own modules
// would be overkill while they're only consumed by <Sidebar />.
// ---------------------------------------------------------------------------

interface SidebarLinkProps {
    item: NavItem;
    onNavigate?: () => void;
}

// Badge colour scheme, keyed by badge label. "Hot" is rendered in gold
// (amber) to give high-priority modules a premium, impressive feel that
// stands apart from the emerald "Popular" pills. Each scheme uses solid
// light-mode backgrounds (``*-100`` bg + ``*-800`` text + ``*-400``
// border) for strong contrast in light mode, with translucent overlays
// in dark mode. Unknown badges fall back to a neutral slate scheme.
const badgeClasses = (badge: string): string => {
    switch (badge.trim().toLowerCase()) {
        case 'hot':
            return [
                'border-amber-400 bg-amber-100 text-amber-800',
                'dark:border-amber-400/50 dark:bg-amber-400/15 dark:text-amber-300',
            ].join(' ');
        case 'popular':
            return [
                'border-emerald-300 bg-emerald-100 text-emerald-700',
                'dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300',
            ].join(' ');
        case 'pro':
            // Premium indigo→violet scheme to set paid-tier PRO modules apart.
            return [
                'border-indigo-400 bg-indigo-100 text-indigo-700',
                'dark:border-violet-500/50 dark:bg-violet-500/15 dark:text-violet-300',
            ].join(' ');
        default:
            return [
                'border-slate-300 bg-slate-100 text-slate-700',
                'dark:border-slate-500/40 dark:bg-slate-500/15 dark:text-slate-300',
            ].join(' ');
    }
};

const SidebarLink = ({ item, onNavigate }: SidebarLinkProps) => {
    const Icon = item.icon;
    // ``end`` is only correct for ``/dashboard`` itself -- without it,
    // every sub-route (``/dashboard/risk-assessment``) would also light
    // up the Dashboard entry. For other items we WANT the active state
    // to follow into nested routes (so /dashboard/risk-assessment/test
    // still highlights "Risk Assessment"), so we drop ``end`` there.
    const matchExact = item.to === '/dashboard';
    return (
        <NavLink
            to={item.to}
            onClick={onNavigate}
            end={matchExact}
            className={({ isActive }) =>
                [
                    'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold',
                    'transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                    isActive
                        ? 'bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 shadow-lg shadow-green-500/10'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-300/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white',
                ].join(' ')
            }
        >
            {({ isActive }) => (
                <>
                    <Icon
                        className={[
                            'shrink-0 transition-colors',
                            isActive
                                ? 'text-green-400 dark:text-emerald-500'
                                : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white',
                        ].join(' ')}
                    />
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                        <span
                            className={[
                                'ml-auto text-[9px] font-bold tracking-wider uppercase',
                                'px-2 py-0.5 rounded-full border',
                                badgeClasses(item.badge),
                            ].join(' ')}
                        >
                            {item.badge}
                        </span>
                    )}
                </>
            )}
        </NavLink>
    );
};

const SectionDivider = () => (
    <div className="my-3 mx-3 h-px bg-slate-300/70 dark:bg-slate-800/80" aria-hidden />
);

// Shared typography for both the static and collapsible section headers so
// the rhythm stays identical regardless of interactivity.
const SECTION_HEADER_TEXT =
    'text-[11px] tracking-wider uppercase font-semibold text-slate-500 dark:text-slate-400 select-none';

interface SectionHeaderProps {
    title: string;
    /** When provided the header renders as a toggle button with a chevron. */
    open?: boolean;
    onToggle?: () => void;
    /** Stable id so the items list can be referenced via aria-controls. */
    panelId?: string;
}

const SectionHeader = ({ title, open, onToggle, panelId }: SectionHeaderProps) => {
    if (!onToggle) {
        return (
            <div className={['px-3 py-2 mt-3', SECTION_HEADER_TEXT].join(' ')}>{title}</div>
        );
    }
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={panelId}
            className={[
                'group w-full flex items-center justify-between gap-2 px-3 py-2 mt-3 rounded-lg',
                'transition-colors hover:text-slate-700 dark:hover:text-slate-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                SECTION_HEADER_TEXT,
            ].join(' ')}
        >
            <span className="opacity-90 group-hover:opacity-100">{title}</span>
            <ChevronIcon open={!!open} className="opacity-60 group-hover:opacity-100" />
        </button>
    );
};

// ---------------------------------------------------------------------------
// Account block — the body of the expandable "Settings" entry. Renders the
// fields requested by the product spec: email, name, membership, status.
// Falls back gracefully while /users/me is in flight or errored so the
// sidebar layout never collapses.
// ---------------------------------------------------------------------------

interface AccountRowProps {
    label: string;
    value: ReactNode;
}

const AccountRow = ({ label, value }: AccountRowProps) => (
    <div className="flex items-start justify-between gap-3 text-xs">
        <span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            {label}
        </span>
        <span
            className="font-semibold text-slate-800 dark:text-slate-100 text-right truncate max-w-[10rem]"
            title={typeof value === 'string' ? value : undefined}
        >
            {value}
        </span>
    </div>
);

const SettingsAccountBlock = () => {
    const currentUser = useCurrentUser();

    if (currentUser.status === 'loading') {
        return (
            <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                Loading account…
            </p>
        );
    }
    if (currentUser.status === 'error') {
        return (
            <p className="px-3 py-2 text-xs text-red-600 dark:text-red-400">
                {currentUser.error}
            </p>
        );
    }

    const { email, full_name, membership_level, is_active } = currentUser.user;
    const statusLabel = is_active ? 'Active' : 'Inactive';
    const statusClasses = is_active
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-amber-600 dark:text-amber-400';

    return (
        <div className="mt-1 mx-2 rounded-xl border border-slate-300/70 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-900/60 p-3 space-y-2">
            <AccountRow label="Email" value={email} />
            <AccountRow label="Name" value={full_name?.trim() || '—'} />
            <AccountRow label="Membership" value={membership_level || '—'} />
            <AccountRow
                label="Status"
                value={<span className={statusClasses}>{statusLabel}</span>}
            />
        </div>
    );
};

// ---------------------------------------------------------------------------
// <Sidebar /> — exported component.
// ---------------------------------------------------------------------------

export interface SidebarProps {
    /**
     * Optional className applied to the <aside> root so a page can fine-tune
     * width or spacing without forking the component.
     */
    className?: string;
}

// localStorage key prefix for persisting each collapsible section's open
// state across reloads. Keyed by the (stable) group title.
const SECTION_STATE_PREFIX = 'forge.sidebar.section.';

const readPersistedOpen = (title: string, fallback: boolean): boolean => {
    if (typeof window === 'undefined') return fallback;
    try {
        const raw = window.localStorage.getItem(SECTION_STATE_PREFIX + title);
        if (raw === 'open') return true;
        if (raw === 'closed') return false;
    } catch {
        /* localStorage may be unavailable (private mode / SSR) — ignore. */
    }
    return fallback;
};

export const Sidebar = ({ className = '' }: SidebarProps) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Per-section open state, keyed by array index. Initialised lazily from
    // localStorage (falling back to each section's defaultOpen) so a user's
    // collapse preferences survive reloads.
    const [openMap, setOpenMap] = useState<Record<number, boolean>>(() => {
        const initial: Record<number, boolean> = {};
        SIDEBAR_SECTIONS.forEach((section, idx) => {
            if (section.collapsible && section.groupTitle) {
                initial[idx] = readPersistedOpen(
                    section.groupTitle,
                    section.defaultOpen ?? true,
                );
            }
        });
        return initial;
    });

    const closeMobile = () => setMobileOpen(false);

    const isSectionOpen = (idx: number, section: NavSection) =>
        openMap[idx] ?? section.defaultOpen ?? true;

    const toggleSection = (idx: number, section: NavSection) => {
        const next = !isSectionOpen(idx, section);
        setOpenMap((m) => ({ ...m, [idx]: next }));
        if (section.groupTitle && typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(
                    SECTION_STATE_PREFIX + section.groupTitle,
                    next ? 'open' : 'closed',
                );
            } catch {
                /* ignore persistence failures */
            }
        }
    };

    const navBody = (
        <nav className="flex-1 overflow-y-auto px-2 py-4" aria-label="Primary">
            {SIDEBAR_SECTIONS.map((section, sectionIdx) => {
                const open = isSectionOpen(sectionIdx, section);
                const panelId = `sidebar-section-${sectionIdx}`;
                return (
                    <div key={sectionIdx} role="group" aria-labelledby={`${panelId}-label`}>
                        {section.groupTitle && (
                            <div id={`${panelId}-label`}>
                                <SectionHeader
                                    title={section.groupTitle}
                                    open={section.collapsible ? open : undefined}
                                    onToggle={
                                        section.collapsible
                                            ? () => toggleSection(sectionIdx, section)
                                            : undefined
                                    }
                                    panelId={panelId}
                                />
                            </div>
                        )}

                        <ul
                            id={panelId}
                            className={[
                                'space-y-1',
                                section.collapsible && !open ? 'hidden' : '',
                            ].join(' ')}
                        >
                            {section.items.map((item) => (
                                <li key={item.to}>
                                    <SidebarLink item={item} onNavigate={closeMobile} />
                                </li>
                            ))}
                        </ul>

                        {sectionIdx < SIDEBAR_SECTIONS.length - 1 && <SectionDivider />}
                    </div>
                );
            })}

            <SectionDivider />

            {/* Settings — expandable entry. Clicking the row toggles the
                Account block; the row itself is a button (not a link)
                because the destination view lives inline. */}
            <div>
                <button
                    type="button"
                    aria-expanded={settingsOpen}
                    aria-controls="sidebar-settings-panel"
                    onClick={() => setSettingsOpen((v) => !v)}
                    className={[
                        'group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold',
                        'transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                        settingsOpen
                            ? 'bg-slate-300/60 dark:bg-slate-800/60 text-slate-900 dark:text-white'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-300/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white',
                    ].join(' ')}
                >
                    <SettingsIcon
                        className={[
                            'shrink-0 transition-colors',
                            settingsOpen
                                ? 'text-green-500 dark:text-emerald-400'
                                : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white',
                        ].join(' ')}
                    />
                    <span className="flex-1 text-left truncate">Settings</span>
                    <ChevronIcon open={settingsOpen} />
                </button>

                {settingsOpen && (
                    <div id="sidebar-settings-panel" className="mt-2">
                        <SettingsAccountBlock />
                    </div>
                )}
            </div>
        </nav>
    );

    return (
        <>
            {/* Mobile trigger — only visible below md. Sits in the top-left so
                it does not collide with the header's right-aligned controls. */}
            <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
                className={[
                    'md:hidden fixed top-3 left-3 z-40 inline-flex items-center justify-center',
                    'h-10 w-10 rounded-xl border-2 border-slate-300 dark:border-slate-700',
                    'bg-slate-200/90 dark:bg-slate-900/90 text-slate-900 dark:text-white',
                    'backdrop-blur transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
                    'hover:bg-slate-300 dark:hover:bg-slate-800',
                ].join(' ')}
            >
                <MenuIcon />
            </button>

            {/* Mobile backdrop. */}
            {mobileOpen && (
                <div
                    className="md:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
                    onClick={closeMobile}
                    aria-hidden
                />
            )}

            <aside
                className={[
                    // Mobile: off-canvas drawer; md+: in-flow persistent column.
                    'fixed md:sticky top-0 left-0 z-50 md:z-auto',
                    'h-screen md:h-[100dvh] w-72 shrink-0',
                    'flex flex-col',
                    'bg-slate-100 dark:bg-slate-950',
                    'border-r border-slate-300 dark:border-slate-800',
                    'transition-transform duration-200 ease-out',
                    mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
                    className,
                ].join(' ')}
                aria-label="Sidebar"
            >
                {/* Brand + mobile close. Mirrors the wordmark in <Layout /> so
                    the visual identity carries over from the header. */}
                <div className="flex items-center justify-between px-4 h-16 border-b border-slate-300 dark:border-slate-800">
                    <div className="brand-wordmark text-lg">
                        Forge <span className="brand-accent">Ultra</span>
                    </div>
                    <button
                        type="button"
                        onClick={closeMobile}
                        aria-label="Close navigation"
                        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-300/60 dark:hover:bg-slate-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {navBody}
            </aside>
        </>
    );
};

export default Sidebar;
